import type { ReactNode } from "react";
import type { FAQItem } from "@/lib/schema";
import { Action, type ActionLink } from "./ConversionPage.Action";

export type { ActionLink };

interface PageHeroProps {
  eyebrow: string;
  title: ReactNode;
  subtitle: ReactNode;
  trustSignals?: string[];
  primaryAction?: ActionLink;
  secondaryAction?: ActionLink;
  compact?: boolean;
}

export function PageHero({
  eyebrow,
  title,
  subtitle,
  trustSignals = [],
  primaryAction,
  secondaryAction,
  compact = false,
}: PageHeroProps) {
  return (
    <section
      className={`mx-auto max-w-3xl px-4 sm:px-6 text-center ${
        compact ? "pt-5 pb-5 sm:pt-8 sm:pb-6" : "pt-8 sm:pt-12 pb-8"
      }`}
    >
      {/* Hero text block is read-only chrome — PostHog logged 19+21 dead
          clicks/wk on the H1 / subtitle paragraph as users tapped expecting
          scroll-to-form (native mobile pattern). The primaryAction button
          below already handles that affordance; mark the text inert so the
          cursor stays default and the click doesn't register as dead. Text
          remains selectable for copy-paste. */}
      <div className="pointer-events-none">
        <p className="select-text text-eyebrow font-bold uppercase tracking-eyebrow text-intent-brand">
          {eyebrow}
        </p>
        <h1
          className={`mt-3 select-text font-serif font-extrabold text-text-primary ${
            compact ? "text-headline sm:text-display" : "text-display"
          }`}
        >
          {title}
        </h1>
        <div
          className={`mx-auto max-w-2xl select-text leading-relaxed text-text-secondary ${
            compact ? "mt-3 text-body" : "mt-4 text-subtitle-small"
          }`}
        >
          {subtitle}
        </div>
      </div>

      {trustSignals.length > 0 ? (
        // Trust badges are pure descriptive chrome — bullet+text combo reads as
        // tappable but never was. pointer-events:none stops the cursor-pointer
        // and silences the dead-click stream PostHog kept logging on
        // /ai-astrologer (Free to start: 440+/wk on the old badge layout).
        // Badges stay visually identical and selectable for copy-paste.
        <div
          className={`conversion-trust-signals pointer-events-none flex flex-wrap justify-center gap-x-5 gap-y-2 text-body-small font-semibold text-text-muted ${
            compact ? "mt-4" : "mt-5"
          }`}
        >
          {trustSignals.map((signal) => (
            <span key={signal} className="inline-flex items-center gap-1.5 select-text">
              <span className="h-1.5 w-1.5 rounded-full bg-intent-brand" aria-hidden />
              {signal}
            </span>
          ))}
        </div>
      ) : null}

      {primaryAction || secondaryAction ? (
        <div
          className={`${compact ? "mt-5" : "mt-7"} flex flex-col sm:flex-row justify-center gap-3`}
        >
          {primaryAction ? <Action action={primaryAction} primary /> : null}
          {secondaryAction ? <Action action={secondaryAction} /> : null}
        </div>
      ) : null}
    </section>
  );
}

export function ContentSection({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`mx-auto max-w-2xl px-4 py-10 text-body leading-relaxed text-text-secondary sm:px-6 sm:py-14 ${className}`}
    >
      {children}
    </section>
  );
}

export function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <h2 className="mb-4 mt-10 font-serif text-title font-bold text-text-primary first:mt-0 sm:text-headline">
      {children}
    </h2>
  );
}

interface CTASectionProps {
  eyebrow?: string;
  title: string;
  subtitle: string;
  href: string;
  cta: string;
  compact?: boolean;
}

export function CTASection({
  eyebrow,
  title,
  subtitle,
  href,
  cta,
  compact = false,
}: CTASectionProps) {
  return (
    <a
      href={href}
      className={
        "group block rounded-2xl border border-border-interactive bg-surface-brand-subtle " +
        (compact ? "p-4" : "p-5 sm:p-6")
      }
    >
      <div className="flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
        <span
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-surface-brand-strong text-title text-text-on-brand"
          aria-hidden
        >
          ॐ
        </span>
        <span className="min-w-0 flex-1">
          {eyebrow ? (
            <span className="block text-eyebrow font-bold uppercase tracking-eyebrow text-intent-brand">
              {eyebrow}
            </span>
          ) : null}
          <span className="block text-subtitle-small font-bold text-text-primary">{title}</span>
          <span className="mt-1 block text-body-small leading-relaxed text-text-secondary">
            {subtitle}
          </span>
        </span>
        <span className="inline-flex min-h-11 shrink-0 items-center justify-center rounded-xl bg-surface-brand-strong px-4 text-button font-bold text-text-on-brand group-hover:opacity-90">
          {cta}
        </span>
      </div>
    </a>
  );
}

export function FAQSection({
  title = "Frequently Asked Questions",
  items,
}: {
  title?: string;
  items: FAQItem[];
}) {
  return (
    <div className="mt-10">
      <SectionHeading>{title}</SectionHeading>
      <div className="space-y-3">
        {items.map((item) => (
          <details
            key={item.question}
            className="group rounded-xl border border-border-default bg-surface-raised"
          >
            <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-4 p-4 text-body font-bold text-text-primary">
              <span>{item.question}</span>
              <span
                className="shrink-0 text-subtitle leading-none text-intent-brand transition-transform group-open:rotate-45"
                aria-hidden
              >
                +
              </span>
            </summary>
            <p className="border-t border-border-subtle px-4 pb-4 pt-3 text-body leading-relaxed text-text-secondary">
              {item.answer}
            </p>
          </details>
        ))}
      </div>
    </div>
  );
}
